import React from 'react';
import ButtonComponent from 'components/button/buttonComponent';
import { twMerge } from 'tailwind-merge';
import OptionsModal, {
  IOptionProps,
} from 'components/optionsModal/OptionsModal';
import DateAppointment from './DateAppointment';
import StartTimeAndTypeAppointment from './StartTimeAndTypeappointment';
import ReasonAndPaymentBlock from './ReasonAndPaymentBlock';
import { UpcomingAppoinment } from '../../interfaces';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { useAppointmentCardHelper } from './helper/useAppointmentCardHelper';
import CustomTooltip from 'components/CustomTooltip';
import moment from 'moment';
import { useCountdown } from 'hooks/useTimer';

interface IUpcomingAppointmentCard extends UpcomingAppoinment {
  appointmentStatus: AppointmentHasuraStatus;
  handleNameClick: () => void;
  tooltipInfo?: boolean;
  options: IOptionProps[];
  handleDetailsClick?: () => void;
}

const UpcomingAppointmentsCardDesktop: React.FC<IUpcomingAppointmentCard> = ({
  appointmentStatus,
  date,
  end,
  status,
  options,
  name,
  appointmentType,
  appointmentId,
  subjectOfAppointment,
  providerId,
  paymentInformation,
  patientId,
  lastProcessedDate,
  paymentSessionOpenedAt,
  handleNameClick,
  handleDetailsClick,
  tooltipInfo,
}) => {
  const {
    loading,
    locale,
    label,
    iconClassNames,
    Icon,
    isNotPaid,
    isJoinable,
    statusClassName,
    notificationClassNames,
    notification,
    opened,
    setOpened,
    hasPastTimeCancelAppointment,
    handleRetryPayment,
    handleJoinAppointment,
  } = useAppointmentCardHelper({
    date,
    appointmentStatus,
    end,
    status,
    appointmentId,
    appointmentType,
    providerId,
    patientId,
    name,
  });

  const { timer } = useCountdown(
    moment(paymentSessionOpenedAt).add(15, 'minutes').toISOString(),
  );

  return (
    <div
      className="max-desktop:hidden w-full"
      data-testid="upcoming-appointment-card-desktop"
    >
      <div className="flex items-center desktop:px-30 px-5 py-4 border border-transparent border-b-gray-opacity-15 bg-neutral-100 rounded-tr-xl rounded-tl-xl">
        <span className="text-title font-semibold text-neutral-800 whitespace-nowrap">
          Appointment with
        </span>
        <ButtonComponent
          type="underline"
          onClick={handleNameClick}
          noUpperCase
          testID={'provider-name'}
          containerClassName="!text-h5"
          className="ml-1 !text-clc-blue !text-h5 whitespace-nowrap"
        >
          {name}
        </ButtonComponent>
        <span
          className={twMerge('mx-3 capitalize', statusClassName)}
          data-testid={`appointment-status-${status}`}
          data-paid-status={!isNotPaid}
        >
          {label || status}
        </span>

        <span>
          {Icon && notification && (
            <Icon
              data-tooltip-id={tooltipInfo && `status-${appointmentId}`}
              className={iconClassNames}
            />
          )}
        </span>

        {!tooltipInfo && (
          <span className={notificationClassNames}>{notification}</span>
        )}

        <CustomTooltip
          id={`status-${appointmentId}`}
          content={notification}
          place="bottom"
        />

        {!hasPastTimeCancelAppointment && (
          <div className="ml-auto">
            <OptionsModal
              opened={opened}
              setOpened={setOpened}
              options={options}
              className="!bg-transparent !border-transparent -mr-5"
              optionsMenuClassName="rounded-md w-[17rem] !border-transparent -left-[14rem]"
              optionsClassName="uppercase text-clc-blue text-sm font-bold whitespace-nowrap"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-9 gap-30 p-5 desktop:px-30 pb-5 pt-2.5 border-b-gray-opacity-15 bg-neutral-100 rounded-br-xl rounded-bl-xl">
        <div className="col-span-1">
          <DateAppointment startDate={date} />
        </div>
        <div className="col-span-2 flex justify-start">
          <StartTimeAndTypeAppointment
            type={appointmentType}
            start={date}
            end={String(end)}
          />
        </div>
        <div className="col-span-3 flex justify-start">
          <ReasonAndPaymentBlock
            subjectOfAppointment={subjectOfAppointment}
            appointmentId={appointmentId}
            paymentInformation={paymentInformation}
            appointmentStatus={appointmentStatus}
            startAt={lastProcessedDate}
          />
        </div>
        <div className="col-span-3 flex items-center justify-end">
          {handleDetailsClick && (
            <ButtonComponent
              type="underline"
              className="!text-sm rounded max-w-[6.75rem] !w-fit !px-0 ml-auto block"
              desktopFullWidth
              testID={'legal-doc-edit-appointment'}
              onClick={handleDetailsClick}
            >
              {locale?.details || 'Details'}
            </ButtonComponent>
          )}
          {isJoinable && (
            <ButtonComponent
              className="!text-sm rounded min-w-fit max-w-[6.75rem] !px-3 ml-auto block"
              desktopFullWidth
              testID={'join-appointment'}
              onClick={handleJoinAppointment}
            >
              {locale?.joinNow}
            </ButtonComponent>
          )}
          {isNotPaid ||
            (appointmentStatus ===
              AppointmentHasuraStatus.PendingAuthorizedPayment &&
              moment().diff(paymentSessionOpenedAt, 'minutes') >= 3 && (
                <>
                  <div className="flex w-full items-center mx-1.5">
                    <span className="text-neutral-800 font-semibold mr-1">
                      Time to retry:
                    </span>
                    <span className="rounded-full font-semibold py-0.5 px-3 desktop:ml-2.5 border text-bright-green border-bright-green bg-green-50">
                      {timer}
                    </span>
                  </div>
                  <ButtonComponent
                    className="!text-sm !w-auto whitespace-nowrap !px-3"
                    desktopFullWidth
                    disabled={loading}
                    testID={'retry-payment'}
                    onClick={handleRetryPayment}
                  >
                    Retry payment
                  </ButtonComponent>
                </>
              ))}
        </div>
      </div>
    </div>
  );
};

export default UpcomingAppointmentsCardDesktop;
